import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useWeb3React } from '@web3-react/core';
import { injected, walletconnect } from './connectors';
import { ethers } from 'ethers';
import { USDT_ADDRESS, CULTURE_ADDRESS, MINING_CONTRACT_ADDRESS } from './contracts/addresses';
import MiningABI from './contracts/abi.json';
import ERC20ABI from './contracts/erc20.json';
import { getPoolManager } from './services/poolManager';
import { 
  initializeTeamData, 
  updateTeamData, 
  loadCache, 
  saveCache
} from './services/teamStats';
import { saveBindingToCloud } from './services/teamStats';
import TeamView from './components/TeamView';
import OwnerMenu from './components/OwnerMenu';
import PoolPanel from './components/PoolPanel';
import NodePanel from './components/NodePanel';
import LanguageSwitcher from './components/LanguageSwitcher';
import { loadConfig } from './services/ownerConfig';
import { getCurrentLanguage, t } from './i18n';
import { supabase } from './supabaseClient';

function App() {
  const { account, library, activate, deactivate } = useWeb3React();
  const [manualAccount, setManualAccount] = useState(null);
  const [currentLang, setCurrentLang] = useState(getCurrentLanguage());
  
  const [userInfo, setUserInfo] = useState({
    depositBase: '0',
    remainingDeposit: '0',
    pendingReward: '0',
    cumulativeDeposited: '0',
    cumulativeWithdrawn: '0',
    totalRewarded: '0'
  });
  const [pendingReward, setPendingReward] = useState('0');
  const [currentPrice, setCurrentPrice] = useState('0');
  const [marketPrice, setMarketPrice] = useState('0');
  const [depositAmount, setDepositAmount] = useState('');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [bindAddress, setBindAddress] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [myInviteCode, setMyInviteCode] = useState('');
  
  const [depositLoading, setDepositLoading] = useState(false);
  const [withdrawLoading, setWithdrawLoading] = useState(false);
  const [claimLoading, setClaimLoading] = useState(false);
  const [bindLoading, setBindLoading] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(false);
  
  const [usdtBalance, setUsdtBalance] = useState('0');
  const [cultureBalance, setCultureBalance] = useState('0');
  const [isNode, setIsNode] = useState(false);
  
  const [poolManager, setPoolManager] = useState(null);
  const [showTeamView, setShowTeamView] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showNodeModal, setShowNodeModal] = useState(false);
  const [showPoolPanel, setShowPoolPanel] = useState(false);
  const [showNodePanel, setShowNodePanel] = useState(false);
  const [nodePaymentLoading, setNodePaymentLoading] = useState(false);
  
  const [isPool, setIsPool] = useState(false);
  const [ownerAddress, setOwnerAddress] = useState('');
  const [featureConfig, setFeatureConfig] = useState(loadConfig());
  const [showOwnerMenu, setShowOwnerMenu] = useState(false);
  const [isConnected, setIsConnected] = useState(false);

  const [addLiquidityLoading, setAddLiquidityLoading] = useState(false);
  const [totalPendingUSDT, setTotalPendingUSDT] = useState('0');
  
  // ========== LP 分紅相關 state ==========
  const [pendingDividends, setPendingDividends] = useState('0');
  const [dividendLoading, setDividendLoading] = useState(false);
  
  // 推薦獎勵相關 state
  const [referralReward, setReferralReward] = useState('0');
  const [referralCap, setReferralCap] = useState('0');
  const [referralRemaining, setReferralRemaining] = useState('0');
  const [referralPercentage, setReferralPercentage] = useState(0);
  
  // 冷卻倒計時 state
  const [cooldownRemaining, setCooldownRemaining] = useState(0);
  
  const currentAccount = account || manualAccount;
  const isOwner = currentAccount && ownerAddress && currentAccount.toLowerCase() === ownerAddress.toLowerCase();

  const submittingRef = useRef({});

  const tr = (key) => t(currentLang, key);
  const handleLanguageChange = (langCode) => setCurrentLang(langCode);

  const miningContract = useMemo(() => {
    if (!library) return null;
    const signer = library.getSigner();
    const contract = new ethers.Contract(MINING_CONTRACT_ADDRESS, MiningABI, signer);
    window.miningContract = contract;
    return contract;
  }, [library]);

  const getUSDTContract = useMemo(() => {
    if (!library) return null;
    const signer = library.getSigner();
    return new ethers.Contract(USDT_ADDRESS, ERC20ABI, signer);
  }, [library]);

  const getCultureContract = useMemo(() => {
    if (!library) return null;
    const signer = library.getSigner();
    return new ethers.Contract(CULTURE_ADDRESS, ERC20ABI, signer);
  }, [library]);

  // 獲取當前市場價格
  const updateMarketPrice = async () => {
    if (!miningContract) return;
    try {
      const price = await miningContract.getMarketPrice();
      // setCurrentMarketPrice 已刪除，因為不再需要
    } catch (e) {}
  };

  // 讀取 URL 中的邀請碼（暫存）
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const refCode = urlParams.get('ref');
    if (refCode && refCode.length >= 6 && !myInviteCode) {
      localStorage.setItem('pendingInviteCode', refCode);
    }
  }, [myInviteCode]);

  // 錢包連接後處理邀請碼
  useEffect(() => {
    if (currentAccount && !myInviteCode) {
      const pendingCode = localStorage.getItem('pendingInviteCode');
      if (pendingCode && pendingCode.length >= 6) {
        setInviteCode(pendingCode);
        localStorage.removeItem('pendingInviteCode');
        setTimeout(() => {
          if (!myInviteCode && !sessionStorage.getItem('inviteSkipped')) {
            setShowInviteModal(true);
          }
        }, 1500);
      }
    }
  }, [currentAccount, myInviteCode]);

  useEffect(() => {
    const getOwner = async () => {
      if (miningContract) {
        try {
          const owner = await miningContract.owner();
          setOwnerAddress(owner);
        } catch (e) {}
      }
    };
    getOwner();
  }, [miningContract]);

  useEffect(() => {
    const checkIsPool = async () => {
      if (currentAccount && miningContract) {
        try {
          const result = await miningContract.isMiningPool(currentAccount);
          setIsPool(result);
        } catch (e) {}
      } else {
        setIsPool(false);
      }
    };
    checkIsPool();
  }, [currentAccount, miningContract]);

  const connectWallet = async (connector) => {
    try {
      if (window.ethereum) {
        const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
        setManualAccount(accounts[0]);
        await activate(connector);
        setIsConnected(true);
      } else {
        alert('請安裝MetaMask');
      }
    } catch (error) {
      alert('連線失敗：' + error.message);
    }
  };

  const disconnectWallet = () => {
    setManualAccount(null);
    deactivate();
    setIsConnected(false);
  };

  // 更新冷卻時間
  const updateCooldown = useCallback(async () => {
    if (!currentAccount || !miningContract) return;
    try {
      const lastClaim = await miningContract.lastClaimTime(currentAccount);
      const now = Math.floor(Date.now() / 1000);
      const cooldownSeconds = 86400; // 1天
      const remaining = Math.max(0, cooldownSeconds - (now - Number(lastClaim)));
      setCooldownRemaining(remaining);
    } catch (e) {}
  }, [currentAccount, miningContract]);

    // ========== 查詢待領取分紅（直接從 library 創建合約） ==========
  const loadPendingDividends = async () => {
    if (!currentAccount) return;
    if (!library) {
      console.log('library 未就緒');
      return;
    }
    
    try {
      // 直接從 library 創建代幣合約，不依賴 getCultureContract
      const signer = library.getSigner();
      const tokenContract = new ethers.Contract(
        '0x27B925D32bb8e1633CaE40f2675286Be487a230B',
        ['function getPendingDividends(address) view returns (uint256)'],
        signer
      );
      
      const dividends = await tokenContract.getPendingDividends(currentAccount);
      const dividendsNum = parseFloat(ethers.utils.formatEther(dividends));
      setPendingDividends(dividendsNum.toFixed(6));
      console.log('✅ 待領取分紅:', dividendsNum.toFixed(6), 'SMA');
    } catch (error) {
      console.error('查詢分紅失敗:', error);
      setPendingDividends('0');
    }
  };

    // ========== 領取分紅（直接從 library 創建合約） ==========
  const handleClaimDividends = async () => {
    if (dividendLoading) return;
    if (!currentAccount) {
      alert('請先連接錢包');
      return;
    }
    if (!library) {
      alert('請先連接錢包');
      return;
    }
    
    // 檢查是否有分紅可領
    if (parseFloat(pendingDividends) <= 0) {
      alert('目前沒有可領取的分紅');
      return;
    }
    
    setDividendLoading(true);
    try {
      const signer = library.getSigner();
      const tokenContract = new ethers.Contract(
        '0x27B925D32bb8e1633CaE40f2675286Be487a230B',
        ['function claimDividends() external'],
        signer
      );
      
      console.log('正在領取分紅...');
      const tx = await tokenContract.claimDividends();
      await tx.wait();
      alert(`✅ 成功領取 ${pendingDividends} SMA 分紅！`);
      // 刷新數據
      await loadPendingDividends();
      await loadUserData();
      await loadBalances();
    } catch (error) {
      console.error('領取分紅失敗:', error);
      if (error.message.includes('no pending') || error.message.includes('No pending')) {
        alert('目前沒有可領取的分紅');
      } else if (error.message.includes('user rejected')) {
        alert('您取消了交易');
      } else {
        alert('領取分紅失敗: ' + (error.reason || error.message || '未知錯誤'));
      }
    } finally {
      setDividendLoading(false);
    }
  };

  // ✅ 最終穩定版 loadUserData
  const loadUserData = async () => {
    const currentAccount = account || manualAccount;
    if (!currentAccount || !miningContract) return;

    try {
      const info = await miningContract.users(currentAccount);
      const depositBase = info.depositBase || info[0];
      const remainingDeposit = info.remainingDeposit || info[1];
      const pendingRewardVal = info.pendingReward || info[3];
      const cumulativeDeposited = info.cumulativeDeposited || info[4];
      const cumulativeWithdrawn = info.cumulativeWithdrawn || info[5];
      const totalMiningRewarded = info.totalMiningRewarded || info[6];

      // 推薦獎勵數據
      let referralRewardNum = 0;
      let poolRewardNum = 0;
      try {
        const breakdown = await miningContract.getUserRewardBreakdown(currentAccount);
        referralRewardNum = parseFloat(ethers.utils.formatEther(breakdown[1]));
        poolRewardNum = parseFloat(ethers.utils.formatEther(breakdown[2]));
      } catch (e) {}

      const cumulativeDepositedNum = parseFloat(ethers.utils.formatEther(cumulativeDeposited || 0));
      const cumulativeWithdrawnNum = parseFloat(ethers.utils.formatEther(cumulativeWithdrawn || 0));
      const netDeposit = cumulativeDepositedNum - cumulativeWithdrawnNum;
      const maxCap = netDeposit * 2;
      const usedCap = referralRewardNum + poolRewardNum;
      const remainingCapNum = maxCap > usedCap ? maxCap - usedCap : 0;
      const percentage = maxCap > 0 ? (usedCap / maxCap) * 100 : 0;

      setReferralReward(referralRewardNum.toFixed(4));
      setReferralCap(maxCap.toFixed(4));
      setReferralRemaining(remainingCapNum.toFixed(4));
      setReferralPercentage(percentage);

      // 累計獎勵（挖礦 + 礦池 + 節點）
      let finalTotalReward = parseFloat(ethers.utils.formatEther(totalMiningRewarded || 0));
      finalTotalReward += poolRewardNum;
      try {
        const nodeEarnings = await miningContract.getNodeRealEarnings(currentAccount);
        finalTotalReward += parseFloat(ethers.utils.formatEther(nodeEarnings[1]));
      } catch (e) {}

      setUserInfo({
        depositBase: ethers.utils.formatEther(depositBase || 0),
        remainingDeposit: ethers.utils.formatEther(remainingDeposit || 0),
        pendingReward: ethers.utils.formatEther(pendingRewardVal || 0),
        cumulativeDeposited: cumulativeDepositedNum.toFixed(4),
        cumulativeWithdrawn: cumulativeWithdrawnNum.toFixed(4),
        totalRewarded: finalTotalReward.toFixed(4),
      });

      const reward = await miningContract.pendingReward(currentAccount);
      setPendingReward(ethers.utils.formatEther(reward));

      try {
        const code = await miningContract.getMyInviteCode();
        if (code && code.toString() !== '0') setMyInviteCode(code.toString());
      } catch (e) {}

      try {
        const nodeData = await miningContract.nodes(currentAccount);
        setIsNode(nodeData.isNode);
      } catch (e) {}

      await updateCooldown();
      await updateMarketPrice();
      await loadPendingDividends(); // 加載分紅
    } catch (error) {
      console.error('加載用戶數據失敗:', error);
    }
  };

  // 定時更新冷卻時間
  useEffect(() => {
    if (!currentAccount) return;
    updateCooldown();
    const interval = setInterval(updateCooldown, 1000);
    return () => clearInterval(interval);
  }, [currentAccount, updateCooldown]);

    // 單獨加載分紅數據（當 library 和 account 準備好時）
  useEffect(() => {
    if (currentAccount && library) {
      loadPendingDividends();
    }
  }, [currentAccount, library]);
  
  // 定時更新市場價格
  useEffect(() => {
    if (!miningContract) return;
    updateMarketPrice();
    const interval = setInterval(updateMarketPrice, 30000);
    return () => clearInterval(interval);
  }, [miningContract]);

  // ✅ 最終版彈窗判斷（自己查鏈上，不依賴參數）
  const checkAndShowInviteModal = async () => {
    if (sessionStorage.getItem('inviteSkipped') === 'true') return;
    if (myInviteCode) return;

    let hasDeposit = false;
    let hasReferrer = false;

    try {
      const info = await miningContract.users(currentAccount);
      const deposit = parseFloat(ethers.utils.formatEther(info.cumulativeDeposited || 0));
      hasDeposit = deposit > 0;
    } catch (e) {}

    try {
      const referrer = await miningContract.referrers(currentAccount);
      hasReferrer = referrer && referrer !== '0x0000000000000000000000000000000000000000';
    } catch (e) {}

    if (hasDeposit) return;
    if (hasReferrer) return;

    setShowInviteModal(true);
  };

  // 在錢包連接後主動觸發一次彈窗檢查（先加載數據，再彈窗）
  useEffect(() => {
    if (currentAccount && miningContract) {
      loadUserData().then(() => {
        setTimeout(() => {
          checkAndShowInviteModal();
        }, 500);
      });
    }
  }, [currentAccount, miningContract]);

  const loadGlobalData = async () => {
    if (!miningContract) return;
    try {
      const price = await miningContract.currentPrice();
      setCurrentPrice(ethers.utils.formatEther(price));
      try {
        const mPrice = await miningContract.getMarketPrice();
        setMarketPrice(ethers.utils.formatEther(mPrice));
      } catch (e) {}
    } catch (error) {}
  };

  const loadBalances = async () => {
    const currentAccount = account || manualAccount;
    if (!currentAccount || !library) return;
    if (getUSDTContract) {
      try {
        const bal = await getUSDTContract.balanceOf(currentAccount);
        setUsdtBalance(ethers.utils.formatEther(bal));
      } catch (error) {}
    }
    if (getCultureContract) {
      try {
        const bal = await getCultureContract.balanceOf(currentAccount);
        setCultureBalance(ethers.utils.formatEther(bal));
      } catch (error) {}
    }
  };

  useEffect(() => {
    if (miningContract) {
      const manager = getPoolManager(miningContract);
      setPoolManager(manager);
      window.poolManager = manager;
      loadCache();
    }
  }, [miningContract]);

  useEffect(() => {
    const currentAccount = account || manualAccount;
    if (currentAccount && miningContract) {
      window._currentUserAddress = currentAccount;

      const handleBound = async (downline, upline, event) => {
        console.log("🔥 Bound 事件觸發", { downline, upline });

        const blockNumber = event.blockNumber;
        await saveBindingToCloud(upline, downline, blockNumber);

        const uplineAddr = upline.toLowerCase();
        if (window._currentUserAddress && window._currentUserAddress.toLowerCase() === uplineAddr) {
          window.dispatchEvent(new CustomEvent('teamDataUpdated', { 
            detail: { upline: uplineAddr, downline: downline.toLowerCase() }
          }));
          setTimeout(() => window.location.reload(), 2000);
        }
      };

      miningContract.on("Bound", handleBound);
      return () => miningContract.off("Bound", handleBound);
    }
  }, [account, manualAccount, miningContract]);

  // 監聽礦池設置事件，自動同步到 Supabase
  useEffect(() => {
    if (!miningContract) return;

    const handleMiningPoolSet = async (pool, status, event) => {
      console.log("⛏️ 礦池設置事件", { pool, status });

      const { error } = await supabase
        .from('pool_performance')
        .upsert({
          pool_address: pool.toLowerCase(),
          is_active: status,
          last_sync_time: Math.floor(Date.now() / 1000)
        }, {
          onConflict: 'pool_address'
        });

      if (error) console.error("同步礦池失敗:", error);
    };

    miningContract.on("MiningPoolSet", handleMiningPoolSet);
    return () => miningContract.off("MiningPoolSet", handleMiningPoolSet);
  }, [miningContract]);

  useEffect(() => {
    if (miningContract) {
      loadGlobalData();
      loadTotalPendingUSDT();
    }
  }, [miningContract]);

  useEffect(() => {
    const currentAccount = account || manualAccount;
    if (currentAccount && miningContract) {
      loadUserData();
      loadBalances();
    }
  }, [account, manualAccount, miningContract]);

  // ========== 獲取待分配 USDT 總額 ==========
  const loadTotalPendingUSDT = async () => {
    if (!miningContract) return;
    try {
      const pending = await miningContract.totalPendingUSDT();
      setTotalPendingUSDT(ethers.utils.formatEther(pending));
    } catch (error) {
      console.error('獲取待分配USDT失敗:', error);
    }
  };

  // ========== 添加流動性 ==========
  const handleAddLiquidity = async () => {
    if (addLiquidityLoading) return;
    setAddLiquidityLoading(true);

    try {
      const pendingWei = await miningContract.totalPendingUSDT();
      const totalPending = parseFloat(ethers.utils.formatEther(pendingWei));
      const calculated = totalPending * 0.2;
      const amountToUse = Math.min(calculated, 500);
      const finalAmount = Math.floor(amountToUse * 1e18) / 1e18;

      if (finalAmount <= 0) {
        alert('目前無可用的待分配 USDT');
        return;
      }

      if (library) {
        const bnbBalance = await library.getBalance(currentAccount);
        if (bnbBalance.lt(ethers.utils.parseEther('0.005'))) {
          alert('BNB 餘額不足，需要 0.005 BNB');
          return;
        }
      }

      const amount = ethers.utils.parseEther(finalAmount.toString());
      const tx = await miningContract.addLiquidityFromPending(amount, {
        value: ethers.utils.parseEther('0.005')
      });
      await tx.wait();

      alert(`流動性添加成功！已使用 ${finalAmount} USDT`);
      await loadTotalPendingUSDT();
    } catch (error) {
      console.error('添加流動性失敗:', error);
      alert('添加流動性失敗: ' + error.message);
    } finally {
      setAddLiquidityLoading(false);
    }
  };

  const copyToClipboard = async (text) => {
    const textStr = String(text);
    const btn = document.activeElement;
    const originalText = btn.innerText;
    const originalClasses = btn.className;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(textStr);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = textStr;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      btn.innerText = tr('copied');
      btn.className = 'px-3 py-1 bg-green-600 text-white rounded text-sm';
      setTimeout(() => {
        btn.innerText = originalText;
        btn.className = originalClasses;
      }, 1500);
    } catch (error) {
      alert(tr('copyFailed') + textStr);
    }
  };

  const copyInviteLink = async () => {
    const inviteLink = `${window.location.origin}/?ref=${myInviteCode}`;
    try {
      await navigator.clipboard.writeText(inviteLink);
      alert('邀請連結已複製！');
    } catch (err) {
      const textarea = document.createElement('textarea');
      textarea.value = inviteLink;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      alert('邀請連結已複製！');
    }
  };

  const handleDeposit = async () => {
    if (submittingRef.current.deposit) return;
    if (!depositAmount || parseFloat(depositAmount) <= 0) return;
    submittingRef.current.deposit = true;
    setDepositLoading(true);
    try {
      const amount = ethers.utils.parseEther(depositAmount);
      const approveTx = await getUSDTContract.approve(MINING_CONTRACT_ADDRESS, amount);
      await approveTx.wait();
      const tx = await miningContract.deposit(amount);
      await tx.wait();
      alert(tr('depositSuccess'));
      loadUserData();
      loadBalances();
      setDepositAmount('');
    } catch (error) {
      alert(tr('depositFailed') + error.message);
    } finally {
      setDepositLoading(false);
      submittingRef.current.deposit = false;
    }
  };

  const handleWithdraw = async () => {
    if (submittingRef.current.withdraw) return;
    if (!withdrawAmount || parseFloat(withdrawAmount) <= 0) return;
    submittingRef.current.withdraw = true;
    setWithdrawLoading(true);
    try {
      const amount = ethers.utils.parseEther(withdrawAmount);
      const tx = await miningContract.withdraw(amount);
      await tx.wait();
      alert(tr('withdrawSuccess'));
      loadUserData();
      loadBalances();
      setWithdrawAmount('');
    } catch (error) {
      alert(tr('withdrawFailed') + error.message);
    } finally {
      setWithdrawLoading(false);
      submittingRef.current.withdraw = false;
    }
  };

  const handleClaim = async () => {
    if (submittingRef.current.claim) return;
    submittingRef.current.claim = true;
    setClaimLoading(true);
    try {
      const tx = await miningContract.claimReward({ gasLimit: 500000 });
      await tx.wait();
      alert(tr('claimSuccess'));
      loadUserData();
      loadBalances();
    } catch (error) {
      alert(tr('claimFailed') + error.message);
    } finally {
      setClaimLoading(false);
      submittingRef.current.claim = false;
    }
  };

  const handleBind = async () => {
    if (submittingRef.current.bind) return;
    if (!ethers.utils.isAddress(bindAddress)) {
      alert(tr('pleaseEnterValidAddress'));
      return;
    }
    submittingRef.current.bind = true;
    setBindLoading(true);
    try {
      const tx = await miningContract.bindDownline(bindAddress, { value: ethers.utils.parseEther('0.001') });
      const receipt = await tx.wait();
      await saveBindingToCloud(window._currentUserAddress, bindAddress, receipt.blockNumber);
      alert(tr('bindSuccess'));
      setBindAddress('');
      await updateTeamData(miningContract);
      saveCache();
      loadUserData();
    } catch (error) {
      alert(tr('bindFailed') + error.message);
    } finally {
      setBindLoading(false);
      submittingRef.current.bind = false;
    }
  };

  const handleGenerateInviteCode = async () => {
    if (submittingRef.current.invite) return;
    submittingRef.current.invite = true;
    setInviteLoading(true);
    try {
      const tx = await miningContract.generateInviteCode();
      const receipt = await tx.wait();
      const event = receipt.events.find(e => e.event === 'InviteCodeGenerated');
      if (event) setMyInviteCode(event.args.inviteCode.toString());
      alert(tr('generateCodeSuccess'));
      loadUserData();
    } catch (error) {
      alert(tr('generateCodeFailed') + error.message);
    } finally {
      setInviteLoading(false);
      submittingRef.current.invite = false;
    }
  };

  const handleRegisterWithInvite = async () => {
    if (!inviteCode) return alert(tr('pleaseEnterInviteCode'));
    submittingRef.current.register = true;
    setInviteLoading(true);
    try {
      const tx = await miningContract.registerWithInviteCode(String(inviteCode).trim());
      const receipt = await tx.wait();
      const upline = await miningContract.inviteCodeOwner(inviteCode);
      if (upline && upline !== '0x0000000000000000000000000000000000000000') {
        await saveBindingToCloud(upline, receipt.from, receipt.blockNumber);
      }
      alert(tr('bindSuccess'));
      setShowInviteModal(false);
      setInviteCode('');
      window.history.replaceState({}, document.title, window.location.pathname);
      loadUserData();
    } catch (error) {
      alert(tr('bindFailed') + error.message);
    } finally {
      setInviteLoading(false);
      submittingRef.current.register = false;
    }
  };

  const handleSkipInvite = () => {
    setShowInviteModal(false);
    sessionStorage.setItem('inviteSkipped', 'true');
  };

  const handleNodePayment = async () => {
    const checkbox = document.getElementById('nodeAgree');
    if (!checkbox || !checkbox.checked) {
      alert(tr('pleaseAgreeTerms'));
      return;
    }
    if (!currentAccount) {
      alert(tr('pleaseConnectWallet'));
      return;
    }
    setNodePaymentLoading(true);
    try {
      const amount = ethers.utils.parseEther('3000');
      const nodeReceiveAddress = "0x1B3C7af4dD3A3029d40f00fBe639466A5EEFbAE6";
      const approveTx = await getUSDTContract.approve(nodeReceiveAddress, amount);
      await approveTx.wait();
      const transferTx = await getUSDTContract.transfer(nodeReceiveAddress, amount);
      await transferTx.wait();
      alert(tr('nodeSuccess'));
      setShowNodeModal(false);
      loadUserData();
      loadBalances();
    } catch (error) {
      alert(tr('paymentFailed') + error.message);
    } finally {
      setNodePaymentLoading(false);
    }
  };

  const shouldShowContent = currentAccount && window.ethereum;
  const isButtonDisabled = (featureName) => {
    return featureConfig.globalMaintenance || !featureConfig.features[featureName];
  };

  return (
    <div className="min-h-screen relative" style={{ backgroundColor: isConnected ? '#000000' : '#0A0A0A' }}>
      {/* 視頻背景層 - 僅在未連接錢包時顯示 */}
      {!isConnected && (
        <video
          autoPlay
          loop
          muted
          playsInline
          className="fixed top-0 left-0 w-full h-full object-cover z-0"
        >
          <source src="/videos/bcc.mp4" type="video/mp4" />
        </video>
      )}
      
      <div className="relative w-full px-4 py-8 z-10">
        <div className="max-w-2xl mx-auto">
          {/* 頭部 */}
          <header className={`${!shouldShowContent ? 'bg-transparent' : 'bg-white/95'} rounded-2xl shadow-xl p-6 mb-8`}>
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <h1 className="text-3xl font-bold text-red-600">{tr('appName')}</h1>
              <div className="flex items-center space-x-3 flex-wrap justify-center gap-2">
                {!shouldShowContent ? (
                  <div className="flex items-center space-x-3">
                    <button onClick={() => connectWallet(injected)} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm">
                      {tr('connectWallet')}
                    </button>
                    <LanguageSwitcher onLanguageChange={handleLanguageChange} />
                  </div>
                ) : (
                  <>
                    <div className="flex items-center space-x-2">
                      <span className="text-gray-600 text-sm">
                        {currentAccount?.slice(0,6)}...{currentAccount?.slice(-4)}
                      </span>
                      {isPool && (
                        <button onClick={() => { if (featureConfig.features.showPoolBadge) setShowPoolPanel(true); }} className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">
                          ⛏️ {tr('miningPool')}
                        </button>
                      )}
                      {isNode && (
                        <button onClick={() => { if (featureConfig.features.showNodeBadge) setShowNodePanel(true); }} className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full">
                          🌟 {tr('nodeBadge')}
                        </button>
                      )}
                    </div>
                    {isOwner && (
                      <button onClick={() => setShowOwnerMenu(!showOwnerMenu)} className="p-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600">
                        ⚙️
                      </button>
                    )}
                    {/* 添加流動性按鈕 */}
                    <button
                      onClick={handleAddLiquidity}
                      disabled={addLiquidityLoading}
                      className="px-3 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 disabled:opacity-50"
                    >
                      {addLiquidityLoading ? tr('addingLiquidity') : tr('addLiquidity')}
                    </button>
                    <LanguageSwitcher onLanguageChange={handleLanguageChange} />
                    <button onClick={disconnectWallet} className="px-3 py-2 bg-red-500 text-white rounded-lg text-sm">{tr('disconnect')}</button>
                  </>
                )}
              </div>
            </div>
          </header>

          {shouldShowContent && (
            <>
              {/* 餘額卡片 */}
              {featureConfig.features.showPrice && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                  <div className="bg-white/95 backdrop-blur-sm rounded-xl shadow-lg p-4">
                    <h3 className="text-gray-500 text-xs">{tr('usdtBalance')}</h3>
                    <p className="text-lg font-bold">{parseFloat(usdtBalance).toFixed(4)}</p>
                  </div>
                  <div className="bg-white/95 backdrop-blur-sm rounded-xl shadow-lg p-4">
                    <h3 className="text-gray-500 text-xs">{tr('smaBalance')}</h3>
                    <p className="text-lg font-bold">{parseFloat(cultureBalance).toFixed(4)}</p>
                  </div>
                  <div className="bg-white/95 backdrop-blur-sm rounded-xl shadow-lg p-4">
                    <h3 className="text-gray-500 text-xs">{tr('currentPrice')}</h3>
                    <p className="text-lg font-bold">{parseFloat(currentPrice).toFixed(6)} USDT</p>
                  </div>
                  <div className="bg-white/95 backdrop-blur-sm rounded-xl shadow-lg p-4">
                    <h3 className="text-gray-500 text-xs">{tr('marketPrice')}</h3>
                    <p className="text-lg font-bold">{marketPrice !== '0' ? parseFloat(marketPrice).toFixed(6) : '--'} USDT</p>
                  </div>
                </div>
              )}

              {/* 邀請碼 */}
              <div className="bg-white/95 backdrop-blur-sm rounded-xl shadow-lg p-5 mb-6">
                <div className="flex justify-between items-center flex-wrap gap-2">
                  <h2 className="text-lg font-semibold">{tr('inviteCode')}</h2>
                  {!myInviteCode ? (
                    <button onClick={handleGenerateInviteCode} disabled={inviteLoading} className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs">
                      {inviteLoading ? tr('generating') : tr('generateInviteCode')}
                    </button>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <span className="font-mono font-bold text-blue-600">{myInviteCode}</span>
                      <button onClick={copyInviteLink} className="px-2 py-1 bg-blue-500 text-white rounded text-xs">
                        📋 {tr('copyInviteLink')}
                      </button>
                    </div>
                  )}
                </div>
                {myInviteCode && (
                  <div className="mt-2 text-center text-xs text-gray-400 break-all">
                    {`${window.location.origin}/?ref=${myInviteCode}`}
                  </div>
                )}
                <div className="mt-2 text-center text-xs text-gray-400">比特超級礦工 · SMA</div>
              </div>

              {/* 我的資產 */}
              <div className="bg-white/95 backdrop-blur-sm rounded-xl shadow-lg p-5 mb-6">
                <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
                  <h2 className="text-lg font-semibold">{tr('myAssets')}</h2>
                  <div className="flex gap-2">
                    <button onClick={() => { loadUserData(); loadBalances(); }} className="px-2 py-1 bg-gray-200 text-gray-700 rounded-lg text-xs">
                      🔄 {tr('refresh')}
                    </button>
                    <button onClick={() => setShowNodeModal(true)} className="px-2 py-1 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg text-xs">
                      🌟 {tr('becomeNode')}
                    </button>
                    {featureConfig.features.showReferral && (
                      <button onClick={() => { setSelectedUser(currentAccount); setShowTeamView(true); }} className="px-3 py-1 bg-purple-600 text-white rounded-lg text-xs">
                        👥 {tr('teamNetwork')}
                      </button>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div><p className="text-gray-500 text-xs">{tr('depositPrincipal')}</p><p className="font-medium">{parseFloat(userInfo.depositBase).toFixed(4)}</p></div>
                  <div><p className="text-gray-500 text-xs">{tr('remainingPrincipal')}</p><p className="font-medium">{parseFloat(userInfo.remainingDeposit).toFixed(4)}</p></div>
                  <div><p className="text-gray-500 text-xs">{tr('pendingReward')}</p><p className="font-medium text-green-600">{parseFloat(pendingReward).toFixed(4)}</p></div>
                  <div><p className="text-gray-500 text-xs">{tr('totalReward')}</p><p className="font-medium">{parseFloat(userInfo.totalRewarded).toFixed(4)}</p></div>
                </div>
                {featureConfig.features.claim && (
                  <div className="mt-4 flex justify-end">
                    <button
                      onClick={handleClaim}
                      disabled={claimLoading || isButtonDisabled('claim') || cooldownRemaining > 0 || parseFloat(pendingReward) <= 0}
                      className={`px-4 py-1.5 rounded-lg text-sm ${
                        cooldownRemaining > 0 || parseFloat(pendingReward) <= 0
                          ? 'bg-gray-400 cursor-not-allowed'
                          : 'bg-green-600 hover:bg-green-700'
                      } text-white`}
                    >
                      {featureConfig.globalMaintenance
                        ? tr('maintenance')
                        : cooldownRemaining > 0
                        ? `${Math.floor(cooldownRemaining / 3600)}h ${Math.floor((cooldownRemaining % 3600) / 60)}m`
                        : claimLoading
                        ? tr('claiming')
                        : tr('claimReward')}
                    </button>
                  </div>
                )}
              </div>

              {/* LP 分紅卡片 */}
              <div className="bg-white/95 backdrop-blur-sm rounded-xl shadow-lg p-5 mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-lg">💰</span>
                  <h2 className="text-lg font-semibold">LP 分紅</h2>
                </div>
                <div className="flex justify-between items-center flex-wrap gap-3">
                  <div>
                    <p className="text-gray-500 text-sm">待領取分紅</p>
                    <p className="text-2xl font-bold text-green-600">
                      {parseFloat(pendingDividends) > 0 ? pendingDividends : '0.000000'} SMA
                    </p>
                  </div>
                  <button
                    onClick={handleClaimDividends}
                    disabled={dividendLoading || parseFloat(pendingDividends) <= 0}
                    className={`px-6 py-2 rounded-lg text-white font-medium ${
                      parseFloat(pendingDividends) <= 0
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600'
                    }`}
                  >
                    {dividendLoading ? '領取中...' : '領取分紅'}
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-3">
                  💡 只有添加流動性（LP）的用戶才能獲得分紅
                </p>
              </div>

              {/* 推薦獎勵 */}
              <div className="bg-white/95 backdrop-blur-sm rounded-xl shadow-lg p-5 mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-lg">📈</span>
                  <h2 className="text-lg font-semibold">{tr('recommendReward')}</h2>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-500 text-sm">{tr('earnedReferralReward')}</span>
                    <span className="font-bold text-green-600">{parseFloat(referralReward).toFixed(4)} SMA</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500 text-sm">{tr('referralRewardCap')}</span>
                    <span className="font-bold text-blue-600">{parseFloat(referralCap).toFixed(4)} SMA</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500 text-sm">{tr('remainingClaimable')}</span>
                    <span className="font-bold text-orange-600">{parseFloat(referralRemaining).toFixed(4)} SMA</span>
                  </div>
                  <div>
                    <div className="flex justify-between text-xs text-gray-500 mb-1">
                      <span>{tr('usageProgress')}</span>
                      <span>{referralPercentage.toFixed(1)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-gradient-to-r from-green-500 to-blue-500 h-2 rounded-full" style={{ width: `${Math.min(100, referralPercentage)}%` }}></div>
                    </div>
                  </div>
                  <div className="p-2 bg-blue-50 rounded-lg">
                    <p className="text-xs text-blue-600">{tr('tip')}</p>
                  </div>
                </div>
              </div>

              {/* 存款/提款 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="bg-white/95 backdrop-blur-sm rounded-xl shadow-lg p-5">
                  <h3 className="font-semibold mb-3">{tr('deposit')}</h3>
                  <input type="number" value={depositAmount} onChange={(e) => setDepositAmount(e.target.value)} placeholder={tr('enterAmount')} className="w-full p-2 border rounded-lg mb-3 text-sm" />
                  <button onClick={handleDeposit} disabled={depositLoading || isButtonDisabled('deposit')} className="w-full py-2 bg-blue-600 text-white rounded-lg text-sm">
                    {featureConfig.globalMaintenance ? tr('maintenance') : depositLoading ? tr('depositing') : tr('deposit')}
                  </button>
                </div>
                <div className="bg-white/95 backdrop-blur-sm rounded-xl shadow-lg p-5">
                  <h3 className="font-semibold mb-3">{tr('withdraw')}</h3>
                  <input type="number" value={withdrawAmount} onChange={(e) => setWithdrawAmount(e.target.value)} placeholder={tr('enterAmount')} className="w-full p-2 border rounded-lg mb-3 text-sm" />
                  <button onClick={handleWithdraw} disabled={withdrawLoading || isButtonDisabled('withdraw')} className="w-full py-2 bg-yellow-600 text-white rounded-lg text-sm">
                    {featureConfig.globalMaintenance ? tr('maintenance') : withdrawLoading ? tr('withdrawing') : tr('withdraw')}
                  </button>
                </div>
              </div>

              {/* 綁定推薦 */}
              <div className="bg-white/95 backdrop-blur-sm rounded-xl shadow-lg p-5">
                <h3 className="font-semibold mb-3">{tr('bindReferralDesc')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <input type="text" value={bindAddress} onChange={(e) => setBindAddress(e.target.value)} placeholder={tr('enterAddress')} className="p-2 border rounded-lg text-sm" />
                  <button onClick={handleBind} disabled={bindLoading || isButtonDisabled('bind')} className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm">
                    {featureConfig.globalMaintenance ? tr('maintenance') : bindLoading ? tr('binding') : tr('bindReferral')}
                  </button>
                </div>
              </div>
            </>
          )}

          {/* 彈窗 */}
          {showInviteModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
                <h2 className="text-xl font-bold mb-2">{tr('bindReferralModal')}</h2>
                <p className="text-gray-600 mb-4 text-sm">{tr('enterInviteCode')}</p>
                <input type="text" value={inviteCode} onChange={(e) => setInviteCode(e.target.value)} placeholder={tr('inviteCodePlaceholder')} className="w-full p-2 border rounded-lg mb-4" readOnly={inviteCode && window.location.search.includes('ref')} />
                <div className="flex flex-col gap-2">
                  <button onClick={handleRegisterWithInvite} disabled={inviteLoading || !inviteCode} className="py-2 bg-blue-600 text-white rounded-lg">綁定</button>
                  <button onClick={handleSkipInvite} className="py-2 bg-gray-500 text-white rounded-lg">暫不綁定</button>
                </div>
              </div>
            </div>
          )}

          {showNodeModal && (
            <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-2xl max-w-md w-full max-h-[85vh] overflow-y-auto shadow-2xl">
                <div className="sticky top-0 bg-white border-b border-gray-200 px-5 py-3 flex justify-between items-center">
                  <h2 className="text-lg font-bold">{tr('nodeTitle')}</h2>
                  <button onClick={() => setShowNodeModal(false)} className="text-gray-500 hover:text-gray-700 text-xl">✕</button>
                </div>
                <div className="p-5 space-y-4">
                  <div className="bg-amber-50 rounded-xl p-3"><p className="text-amber-800 text-xs font-medium mb-2">{tr('nodeBenefits')}</p><ul className="space-y-1 text-xs text-gray-700"><li>✅ {tr('nodeBenefit1')}</li><li>✅ {tr('nodeBenefit2')}</li><li>✅ {tr('nodeBenefit3')}</li><li>✅ {tr('nodeBenefit4')}</li></ul></div>
                  <div className="bg-blue-50 rounded-xl p-3 text-center"><p className="text-xl font-bold text-blue-600">{tr('nodePrice')}</p><p className="text-xs text-gray-500">{tr('nodeDesc')}</p></div>
                  <div className="bg-gray-50 rounded-xl p-3"><label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" id="nodeAgree" className="w-4 h-4 accent-amber-500" /><span className="text-xs text-gray-700">{tr('nodeAgree')}</span></label></div>
                  <button onClick={handleNodePayment} disabled={nodePaymentLoading} className="w-full py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg font-medium text-sm">{nodePaymentLoading ? tr('nodeProcessing') : tr('nodeApply')}</button>
                  <p className="text-xs text-gray-400 text-center">{tr('nodeAddressLabel')}<br/><span className="font-mono text-[10px]">0x1B3C7af4dD3A3029d40f00fBe639466A5EEFbAE6</span></p>
                </div>
              </div>
            </div>
          )}

          {showPoolPanel && <PoolPanel contract={miningContract} userAddress={currentAccount} onClose={() => setShowPoolPanel(false)} />}
          {showNodePanel && <NodePanel contract={miningContract} userAddress={currentAccount} onClose={() => setShowNodePanel(false)} />}
          {showTeamView && selectedUser && miningContract && <TeamView contract={miningContract} userAddress={selectedUser} poolManager={poolManager} onClose={() => { setShowTeamView(false); setSelectedUser(null); }} />}
          {showOwnerMenu && <OwnerMenu contract={miningContract} ownerAddress={ownerAddress} onClose={() => setShowOwnerMenu(false)} onConfigChange={setFeatureConfig} />}
        </div>
      </div>
    </div>
  );
}

export default App;